/**
 * Reconciling a wallet's own monthly screen against the ledger (Phase 1, step 1.6; B3).
 *
 * MoMo does not offer an export, so the numbers are typed in from its "Quản lý chi tiêu"
 * screen once a month. What reaches the ledger is `reported − already_recorded` per category:
 * anything typed in by hand during the month is already there, and posting the reported
 * figure whole would count it twice (spec v2 §26).
 *
 * `already_recorded` comes from `app.account_month_expense_by_category` — the same function
 * `app.confirm_monthly_summary` reads, so the preview and the entry cannot mean different
 * things by it.
 */
import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

export type LedgerTotal = Readonly<{ categoryId: string; amount: number }>;

export type SummaryState = Readonly<{
  id: string;
  status: 'draft' | 'confirmed';
  confirmedEntryId: string | null;
  /** Where the draft came from — the import tool stamps itself here (e.g. "Nhập từ ảnh MoMo"). */
  note: string | null;
  updatedAt: string;
  items: ReadonlyArray<{ categoryId: string; reportedAmount: number }>;
}>;

export async function fetchAlreadyRecorded(
  accountId: string,
  year: number,
  month: number,
): Promise<readonly LedgerTotal[]> {
  const { data, error } = await supabase.schema('app').rpc('account_month_expense_by_category', {
    p_account_id: accountId,
    p_year: year,
    p_month: month,
  });
  if (error) throw appErrorException(error);

  return (data ?? []).map((row) => ({
    categoryId: row.category_id ?? '',
    amount: Number(row.amount ?? 0),
  }));
}

/** The reconciliation already started (or finished) for this account and month, if any. */
export async function fetchSummary(
  accountId: string,
  year: number,
  month: number,
): Promise<SummaryState | null> {
  const { data, error } = await supabase
    .from('external_monthly_summaries')
    .select('id, status, confirmed_entry_id, note, updated_at')
    .eq('source_account_id', accountId)
    .eq('period_year', year)
    .eq('period_month', month)
    .maybeSingle();
  if (error) throw appErrorException(error);
  if (data === null) return null;

  const { data: items, error: itemsError } = await supabase
    .from('external_monthly_summary_items')
    .select('category_id, reported_amount')
    .eq('summary_id', data.id);
  if (itemsError) throw appErrorException(itemsError);

  return {
    id: data.id,
    status: data.status as 'draft' | 'confirmed',
    confirmedEntryId: data.confirmed_entry_id,
    note: data.note,
    updatedAt: data.updated_at,
    items: (items ?? []).map((row) => ({
      categoryId: row.category_id,
      reportedAmount: Number(row.reported_amount),
    })),
  };
}

/**
 * Saves what was typed in, as a draft. Items are replaced wholesale — they are the transcript
 * of one screen, not history, and a half-updated transcript is worse than none.
 */
export async function saveDraft(input: {
  householdId: string;
  subjectId: string;
  accountId: string;
  year: number;
  month: number;
  items: ReadonlyArray<{ categoryId: string; reportedAmount: number }>;
  existingId: string | null;
}): Promise<string> {
  let summaryId = input.existingId;

  if (summaryId === null) {
    const { data, error } = await supabase
      .from('external_monthly_summaries')
      .insert({
        household_id: input.householdId,
        subject_id: input.subjectId,
        source_account_id: input.accountId,
        period_year: input.year,
        period_month: input.month,
      })
      .select('id')
      .single();
    if (error) throw appErrorException(error);
    summaryId = data.id;
  }

  const { error: clearError } = await supabase
    .from('external_monthly_summary_items')
    .delete()
    .eq('summary_id', summaryId);
  if (clearError) throw appErrorException(clearError);

  const { error: insertError } = await supabase.from('external_monthly_summary_items').insert(
    input.items.map((item) => ({
      household_id: input.householdId,
      summary_id: summaryId,
      category_id: item.categoryId,
      reported_amount: item.reportedAmount,
    })),
  );
  if (insertError) throw appErrorException(insertError);

  return summaryId;
}

/** Returns the entry it posted, or null when the month needed no correction at all. */
export async function confirmSummary(summaryId: string): Promise<string | null> {
  const { data, error } = await supabase
    .schema('app')
    .rpc('confirm_monthly_summary', { p_summary_id: summaryId });
  if (error) throw appErrorException(error);
  return data ?? null;
}
